<script setup lang="ts">

import Close from "@icons/MaterialSymbolsClose.svg"
import { ref, watch } from "vue";

const props = withDefaults(defineProps<{
	show?: boolean;
	size?: "xs" | "s" | "m" | 'l' | 'xl';
	closeable?: boolean;
}>(), {
	show: true,
	size: "m",
	closeable: false,
});

const emit = defineEmits<{
	close: [];
}>();

defineOptions({
	inheritAttrs: false,
})

//keep the modal in the DOM a bit after closing to allow the leave animation to finish
const isShowing = ref(false)
watch(() => props.show, (newVal) => {
	if (newVal) {
		isShowing.value = true;
	}
}, { immediate: true });

// Track mousedown to prevent false click-outside triggers
const mouseDownOnBackground = ref(false);

function handleMouseUp(e: MouseEvent) {
	// If mouseup is not on background, reset the flag to prevent closing
	if (e.target !== e.currentTarget) {
		mouseDownOnBackground.value = false;
	}
}

function handleBackgroundClick(e: MouseEvent) {
	// Only close if clicking on background (not bubbled from children) and mousedown also happened on background
	if (e.target === e.currentTarget && mouseDownOnBackground.value) {
		emit('close');
	}
	mouseDownOnBackground.value = false;
}

</script>



<template>
	<Teleport to="body" v-if="isShowing">
		<Transition name="modal" appear @after-leave="isShowing = false" @leave-cancelled="isShowing = false">
			<div class="modal-background" v-if="show" @mousedown.self="mouseDownOnBackground = true" @mouseup="handleMouseUp"
				@click="handleBackgroundClick">
				<div class="modal" :class="`size-${props.size}`">
					<Close class="close-icon" v-if="closeable" @click="$emit('close')" />
					<div class="modal-content" v-bind="$attrs">
						<slot />
					</div>
				</div>
			</div>
		</Transition>
	</Teleport>
</template>


<style scoped lang="scss">
.modal-background {
	position: fixed;
	backdrop-filter: grayscale(30%) blur(1px) brightness(40%);
	background: rgba(0, 0, 0, 0.0);
	transition: backdrop-filter .5s;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 1010;
	display: flex;
	align-items: center;
	justify-content: center;
}

.modal {
	background: white;
	border-radius: 8px;
	box-shadow: 0 2px 10px rgba(0, 0, 0, .3);
	max-width: 500px;
	width: 100%;

	transition: transform .3s, opacity .3s;

	position: relative;

	min-width: 200px;
	max-width: 95%;
	max-height: 100vh;
	max-height: 100svh;
	font-size: 16px;

	.modal-content {
		padding: 2rem 1.5rem;
		overflow-y: auto;
		max-height: 100vh;
		max-height: 100svh;
		display: flex;
		flex-flow: column nowrap;
		align-items: center;
		justify-content: flex-start;
		min-height: 200px;
	}

	&.size-xs {
		max-width: min(95%, 280px);
	}

	&.size-s {
		max-width: min(95%, 400px);
	}

	&.size-m {
		max-width: min(95%, 600px);
	}

	&.size-l {
		max-width: min(95%, 800px);
	}

	&.size-xl {
		max-width: 95%;
	}

	.close-icon {
		position: absolute;
		top: .5rem;
		right: .5rem;
		width: 1.5rem;
		height: 1.5rem;
		cursor: pointer;
		z-index: 10;
	}
}


.modal-enter-from {
	backdrop-filter: none;

	.modal {
		transform: scale(1.1);
		opacity: 0;
	}
}

.modal-leave-to {
	backdrop-filter: none;

	.modal {
		transform: scale(0.9);
		opacity: 0;
	}
}
</style>

<style lang="scss">
/* leak for other components */

.alert-bottom {
	margin-top: auto;
	display: flex;
	flex-flow: row wrap;
	align-items: flex-end;
	justify-content: center;
}

.alert-bottom button {
	margin-top: .5rem;
}

.alert h2,
.modal h2 {
	font-size: 24px;
	color: #888;
	font-weight: bold;
	margin-top: 0;
}
</style>